import React, { useEffect, useState } from "react";
import { Alert, Spinner } from "react-bootstrap";
import PrettyContainer from "../../prettyContainer/PrettyContainer";
import { getMitchellProxy, testMitchellProxy, saveMitchellProxy } from "../../../helpers/api";

const AZUL = "#3790ed";
const MUTED = "#6b7a90";

const vacio = { host: "", port: "", user: "", pass: "", protocol: "http", prodemand: "", truckseries: "" };

export default function MitchellProxyPage() {
  const [form, setForm] = useState(vacio);
  const [hasPass, setHasPass] = useState(false);
  const [info, setInfo] = useState({ por: null, cuando: null });
  const [loading, setLoading] = useState(true);
  const [probando, setProbando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const cargar = () => {
    setLoading(true);
    getMitchellProxy()
      .then((d) => {
        setForm({ host: d.host || "", port: d.port || "", user: d.user || "", pass: "", protocol: d.protocol || "http", prodemand: d.prodemand || "", truckseries: d.truckseries || "" });
        setHasPass(!!d.hasPass);
        setInfo({ por: d.actualizadoPor || null, cuando: d.actualizado || null });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(cargar, []);

  const cambiar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  // La clave del proxy es OPCIONAL (en blanco = se conserva la guardada), por eso
  // NO entra aqui. Las cuentas llegan prellenadas. Solo exigimos IP/puerto/usuario.
  const faltan = () => !form.host.trim() || !String(form.port).trim() || !form.user.trim();

  const probar = () => {
    if (faltan() || probando || guardando) return;
    setMsg({ type: "", text: "" });
    setProbando(true);
    testMitchellProxy(form)
      .then((r) => setMsg({ type: r.ok ? "success" : "warning", text: r.message || (r.ok ? "Works" : "Did not work") }))
      .catch((r) => setMsg({ type: "warning", text: (r && r.message) || "Could not test." }))
      .finally(() => setProbando(false));
  };

  const guardar = () => {
    if (faltan() || probando || guardando) return;
    setMsg({ type: "", text: "" });
    setGuardando(true);
    saveMitchellProxy(form)
      .then((r) => {
        setMsg({ type: r.ok ? "success" : "warning", text: r.message || "Done" });
        if (r.ok) cargar();
      })
      .catch((r) => setMsg({ type: "warning", text: (r && r.message) || "Could not save." }))
      .finally(() => setGuardando(false));
  };

  const campo = (etiqueta, campoNombre, tipo, placeholder) => (
    <div style={{ marginBottom: "12px" }}>
      <label style={{ display: "block", fontWeight: 700, color: "#1d2735", marginBottom: "4px", fontSize: "0.9rem" }}>
        {etiqueta}
      </label>
      <input
        type={tipo}
        value={form[campoNombre]}
        onChange={cambiar(campoNombre)}
        placeholder={placeholder}
        autoComplete="off"
        disabled={probando || guardando}
        style={{
          width: "100%", padding: "10px 12px", borderRadius: "10px",
          border: "2px solid #e6ebf2", fontSize: "0.95rem", boxSizing: "border-box",
        }}
      />
    </div>
  );

  const boton = (texto, onClick, principal, cargando) => (
    <button
      onClick={onClick}
      disabled={probando || guardando || faltan()}
      style={{
        flex: 1, padding: "12px 16px", borderRadius: "12px", border: "none",
        fontWeight: 800, cursor: probando || guardando || faltan() ? "not-allowed" : "pointer",
        color: principal ? "#fff" : AZUL,
        background: principal ? AZUL : "#eef4fd",
        opacity: probando || guardando || faltan() ? 0.6 : 1,
      }}
    >
      {cargando ? <Spinner animation="border" size="sm" /> : texto}
    </button>
  );

  return (
    <PrettyContainer tittle="MITCHELL PROXY">
      <div style={{ paddingTop: "0.6em", maxWidth: "520px" }}>
        <p style={{ color: MUTED }}>
          The residential proxy (US IP) that ProDemand and TruckSeries use to reach Mitchell,
          plus each one's <b>Mitchell account number</b>. If Mitchell blocks the IP, buy a new
          <b> static residential</b> proxy and enter it here; or change an account number below.
          Press <b>Test</b> to check the proxy, then <b>Save &amp; restart</b>. Only what you
          changed restarts. You can leave the proxy password blank to keep the saved one.
          This panel is only for CardexApp — it is separate from ORIGO503.
        </p>

        {loading ? (
          <div style={{ textAlign: "center", padding: "20px", color: MUTED }}>
            <Spinner animation="border" size="sm" /> Loading…
          </div>
        ) : (
          <>
            {info.por && (
              <div style={{ color: MUTED, fontSize: "0.82rem", marginBottom: "10px" }}>
                Last change by <b>{info.por}</b>
                {info.cuando ? " · " + new Date(info.cuando).toLocaleString() : ""}
                {hasPass ? " · a password is saved (type it again to change it)" : ""}
              </div>
            )}

            {msg.text && <Alert variant={msg.type}>{msg.text}</Alert>}

            {campo("IP / Host", "host", "text", "e.g. 48.45.163.44")}
            {campo("Port", "port", "text", "e.g. 46168")}
            {campo("User", "user", "text", "proxy username")}
            {campo("Password", "pass", "password", hasPass ? "•••••• (leave blank to keep it)" : "proxy password")}

            <div style={{ height: "1px", background: "#e6ebf2", margin: "18px 0 14px" }} />
            <div style={{ fontWeight: 800, color: "#1d2735", fontSize: "0.95rem", marginBottom: "8px" }}>
              Mitchell account numbers
            </div>
            <p style={{ color: MUTED, fontSize: "0.85rem", marginTop: 0 }}>
              Change only the one you need and press <b>Save &amp; restart</b>: only that service
              restarts (the other keeps running). Changing the proxy above restarts both.
            </p>
            {campo("ProDemand account", "prodemand", "text", "e.g. 3127138")}
            {campo("TruckSeries account", "truckseries", "text", "e.g. 3127005")}

            <div style={{ display: "flex", gap: "12px", marginTop: "6px" }}>
              {boton("Test", probar, false, probando)}
              {boton("Save & restart", guardar, true, guardando)}
            </div>

            {(probando || guardando) && (
              <div style={{ color: MUTED, fontSize: "0.85rem", marginTop: "10px" }}>
                <Spinner animation="border" size="sm" />{" "}
                {guardando ? "Testing and applying… (gateways restart, ~15s)" : "Testing the proxy… (~15s)"}
              </div>
            )}
          </>
        )}
      </div>
    </PrettyContainer>
  );
}
